/**
 * What Pluto says about itself while a turn is in flight, and afterwards.
 *
 * The server names each tool as it starts, so the waiting message can say what
 * is actually happening. Nothing here may claim work Pluto has not been
 * reported as doing: a phrase that mentions the web belongs only to a web tool.
 */

/** The plain name of a tool, used on the pills under a finished answer. */
const TOOL_LABELS: Record<string, string> = {
  find_companies: "Company search",
  research_company: "Company research",
  search_knowledge: "Bloodline documents",
  find_contract_awards: "Contract awards",
  resolve_industry: "Industry classification",
  describe_data_sources: "Data sources",
  search_companies: "Company lookup",
  get_company: "Company profile",
  get_company_timeline: "Company timeline",
  run_filter: "Saved filter",
  get_company_details: "Company details",
  draft_outreach: "Outreach drafting",
  web_search: "Web search",
  fetch_web_page: "Web page",
};

export const plutoToolLabel = (name: string) => TOOL_LABELS[name] ?? name;

const TOOL_STATUS: Record<string, string> = {
  find_companies: "Sniffing out companies…",
  research_company: "Digging up the whole story…",
  search_knowledge: "Sniffing through Bloodline documents…",
  find_contract_awards: "Digging up contract awards…",
  resolve_industry: "Sniffing out the right industry…",
  describe_data_sources: "Checking which trails exist…",
  search_companies: "Sniffing out that company…",
  get_company: "Fetching the company profile…",
  get_company_timeline: "Fetching the timeline…",
  run_filter: "Chasing down a saved filter…",
  get_company_details: "Fetching the company details…",
  draft_outreach: "Writing your outreach…",
  web_search: "Chasing scents across the web…",
  fetch_web_page: "Fetching that page…",
};

/**
 * Shown in turn while Pluto is working without a tool, which is most of a
 * thinking pause. They stay vague on purpose, because at that point nobody
 * knows yet whether any records or pages will be opened at all — a phrase that
 * named the work would be guessing at it.
 *
 * A bare verb carries as well as a whole clause and reads lighter on the third
 * pass, so the two shapes alternate rather than settling into one. Long enough
 * a list that a slow turn does not visibly loop.
 */
export const PLUTO_WAITING_PHRASES = [
  "Sniffing out an answer…",
  "Snuffling…",
  "Barking up a few trees…",
  "Nosing about…",
  "Chasing down the details…",
  "Digging…",
  "Begging the data to talk…",
  "Chewing it over…",
  "Following the scent…",
  "Rummaging…",
  "Circling back around…",
  "Wagging…",
  "Perking up an ear…",
  "Scampering off after it…",
  "Woofing under its breath…",
  "Doing zoomies…",
  "Burrowing in…",
  "Shaking it out…",
] as const;

/**
 * The tools a turn has reached, in order, once one more is reported.
 *
 * A turn is announced tool by tool and the same tool can arrive twice in a row:
 * a retry replays the turn from the start, and a round can call a tool it has
 * already used. Repeats are folded into the step they repeat so the list reads
 * as the work rather than as the wire, while a tool returned to after a
 * different one still earns its own step, because that is a real second look.
 */
export const plutoToolSteps = (steps: readonly string[], started: string): readonly string[] =>
  steps[steps.length - 1] === started ? steps : [...steps, started];

/**
 * The headline while a turn is in flight. A tool that is running names itself;
 * an unrecognised one still beats silence, so it borrows a plain phrasing.
 */
export const plutoWaitingHeadline = (activeToolName: string | null, rotation: number): string => {
  if (activeToolName === null) {
    const phrases = PLUTO_WAITING_PHRASES;
    return phrases[Math.abs(rotation) % phrases.length] ?? phrases[0];
  }

  return TOOL_STATUS[activeToolName] ?? `Sniffing around ${plutoToolLabel(activeToolName)}…`;
};

/** The quieter second line, which spells the tool out without the dog voice. */
export const plutoWaitingDetail = (activeToolName: string | null): string =>
  activeToolName === null
    ? "Keeping the rest of this conversation in mind."
    : `Using ${plutoToolLabel(activeToolName)}.`;
